import React from 'react';

/**
 * SEO component — uses React 19's native document metadata support.
 * Any <title>, <meta>, <link>, or <script> tags rendered here are
 * automatically hoisted into <head> by React.
 *
 * Usage:
 *   <SEO
 *     title="Expertise Immobilière | ATAA REALTY"
 *     description="..."
 *     canonical="https://ataarealty.com/services/expertise"
 *     ogImage="https://ataarealty.com/og-image.png"
 *     jsonLd={{ ...schema }}
 *   />
 */

const SITE_URL = 'https://ataarealty.com';
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;
const SITE_NAME = 'ATAA REALTY';

const SEO = ({
  title,
  description,
  canonical,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = 'website',
  keywords,
  noindex = false,
  jsonLd,
}) => {
  const fullTitle = title || `${SITE_NAME} — Expertise & Évaluation Immobilière au Maroc`;
  const url = canonical || SITE_URL;

  return (
    <>
      <title>{fullTitle}</title>

      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}

      <link rel="canonical" href={url} />

      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta
          name="robots"
          content="index, follow, max-image-preview:large, max-snippet:-1"
        />
      )}

      {/* Open Graph */}
      <meta property="og:locale" content="fr_MA" />
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={ogImage} />

      {/* Optional JSON-LD structured data injected per page */}
      {jsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      )}
    </>
  );
};

export default SEO;
